import api from './api';

// Types
export interface AdminDashboardStats {
  total_tenants: number;
  active_tenants: number;
  inactive_tenants: number;
  monthly_revenue: number;
  monthly_expenses: number;
  net_profit: number;
  profit_margin: number;
  pending_payments: number;
  overdue_payments: number;
}

export interface MonthlyRevenueItem {
  month: string;
  revenue: number;
  tenant_count: number;
}

export interface MonthlyExpenseItem {
  month: string;
  total: number;
  by_category: Record<string, number>;
}

export interface ProfitHistoryItem {
  month: string;
  revenue: number;
  expenses: number;
  profit: number;
  margin: number;
}

export interface AdminAlert {
  type: string;
  message: string;
  tenant_id: number | null;
  tenant_name: string | null;
  severity: string;
  created_at: string;
}

export interface Subscription {
  id: number;
  tenant_id: number;
  plan: string;
  status: string;
  price_monthly: number;
  start_date: string | null;
  end_date: string | null;
  payment_status: string;
  last_payment_date: string | null;
  next_payment_date: string | null;
  notes: string | null;
  payment_reported_at: string | null;
  payment_reported_reference: string | null;
  mp_preapproval_id: string | null;
  mp_init_point: string | null;
  mp_status: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface TenantWithSubscription {
  id: number;
  name: string;
  slug: string;
  address: string | null;
  phone: string | null;
  email: string | null;
  is_active: boolean;
  online_store_enabled?: boolean;
  store_published?: boolean;
  fiscal_enabled?: boolean;
  created_at: string | null;
  subscription: Subscription | null;
  user_count: number;
}

export interface SubscriptionCreate {
  tenant_id: number;
  plan: string;
  status: string;
  price_monthly: number;
  payment_status: string;
  start_date?: string | null;
  next_payment_date?: string | null;
  notes?: string | null;
}

export interface SubscriptionUpdate {
  plan?: string;
  status?: string;
  price_monthly?: number;
  payment_status?: string;
  last_payment_date?: string | null;
  start_date?: string | null;
  next_payment_date?: string | null;
  end_date?: string | null;
  notes?: string | null;
}

export interface BusinessExpense {
  id: number;
  category: string;
  description: string;
  amount: number;
  currency: string;
  expense_date: string;
  is_recurring: boolean;
  recurring_period: string | null;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface BusinessExpenseCreate {
  category: string;
  description: string;
  amount: number;
  currency: string;
  expense_date: string;
  is_recurring: boolean;
  recurring_period?: string | null;
  notes?: string | null;
}

export interface BusinessExpenseUpdate {
  category?: string;
  description?: string;
  amount?: number;
  currency?: string;
  expense_date?: string;
  is_recurring?: boolean;
  recurring_period?: string | null;
  notes?: string | null;
}

export interface TenantUpdate {
  name?: string;
  address?: string;
  phone?: string;
  email?: string;
  is_active?: boolean;
  online_store_enabled?: boolean;
  store_published?: boolean;
  fiscal_enabled?: boolean;
}

export interface TenantWithAdminCreate {
  tenant_name: string;
  tenant_slug: string;
  tenant_address?: string | null;
  tenant_phone?: string | null;
  tenant_email?: string | null;
  admin_email: string;
  admin_full_name: string;
}

export interface TenantWithAdminResponse {
  tenant: {
    id: number;
    name: string;
    slug: string;
    address: string | null;
    phone: string | null;
    email: string | null;
    is_active: boolean;
    created_at: string;
  };
  admin_email: string;
  admin_name: string;
  temporary_password: string | null;
  email_sent: boolean;
}

export interface TenantUser {
  id: number;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  created_at: string;
}

// API Service
export const adminService = {
  // Dashboard
  getStats: async (): Promise<AdminDashboardStats> => {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  getRevenueHistory: async (months: number = 6): Promise<{ data: MonthlyRevenueItem[] }> => {
    const response = await api.get('/admin/stats/revenue', { params: { months } });
    return response.data;
  },

  getExpenseHistory: async (months: number = 6): Promise<{ data: MonthlyExpenseItem[] }> => {
    const response = await api.get('/admin/stats/expenses', { params: { months } });
    return response.data;
  },

  getProfitHistory: async (months: number = 6): Promise<{ data: ProfitHistoryItem[] }> => {
    const response = await api.get('/admin/stats/profit', { params: { months } });
    return response.data;
  },

  getAlerts: async (): Promise<{ alerts: AdminAlert[]; total: number }> => {
    const response = await api.get('/admin/alerts');
    return response.data;
  },

  // Tenants
  getTenants: async (): Promise<TenantWithSubscription[]> => {
    const response = await api.get('/admin/tenants');
    return response.data;
  },

  getTenant: async (id: number): Promise<TenantWithSubscription> => {
    const response = await api.get(`/admin/tenants/${id}`);
    return response.data;
  },

  updateTenant: async (id: number, data: TenantUpdate): Promise<TenantWithSubscription> => {
    const response = await api.put(`/admin/tenants/${id}`, data);
    return response.data;
  },

  createTenant: async (data: TenantWithAdminCreate): Promise<TenantWithAdminResponse> => {
    const response = await api.post('/admin/tenants', data);
    return response.data;
  },

  getTenantUsers: async (tenantId: number): Promise<TenantUser[]> => {
    const response = await api.get(`/admin/tenants/${tenantId}/users`);
    return response.data;
  },

  // Subscriptions
  getSubscriptions: async (): Promise<Subscription[]> => {
    const response = await api.get('/admin/subscriptions');
    return response.data;
  },

  getSubscription: async (id: number): Promise<Subscription> => {
    const response = await api.get(`/admin/subscriptions/${id}`);
    return response.data;
  },

  createSubscription: async (data: SubscriptionCreate): Promise<Subscription> => {
    const response = await api.post('/admin/subscriptions', data);
    return response.data;
  },

  updateSubscription: async (id: number, data: SubscriptionUpdate): Promise<Subscription> => {
    const response = await api.put(`/admin/subscriptions/${id}`, data);
    return response.data;
  },

  // Expenses
  getExpenses: async (filters?: {
    category?: string;
    date_from?: string;
    date_to?: string;
    limit?: number;
  }): Promise<BusinessExpense[]> => {
    const response = await api.get('/admin/expenses', { params: filters });
    return response.data;
  },

  getExpense: async (id: number): Promise<BusinessExpense> => {
    const response = await api.get(`/admin/expenses/${id}`);
    return response.data;
  },

  createExpense: async (data: BusinessExpenseCreate): Promise<BusinessExpense> => {
    const response = await api.post('/admin/expenses', data);
    return response.data;
  },

  updateExpense: async (id: number, data: BusinessExpenseUpdate): Promise<BusinessExpense> => {
    const response = await api.put(`/admin/expenses/${id}`, data);
    return response.data;
  },

  deleteExpense: async (id: number): Promise<void> => {
    await api.delete(`/admin/expenses/${id}`);
  },

  // Impersonation
  impersonateTenant: async (tenantId: number): Promise<{
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
    tenant_id: number;
    tenant_name: string;
    impersonating: boolean;
  }> => {
    const response = await api.post(`/admin/tenants/${tenantId}/impersonate`);
    return response.data;
  },

  stopImpersonation: async (): Promise<{
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
    impersonating: boolean;
  }> => {
    const response = await api.post('/admin/stop-impersonation');
    return response.data;
  },

  // Reset Password
  resetUserPassword: async (userId: number): Promise<{
    success: boolean;
    user_email: string;
    user_name: string;
    email_sent: boolean;
    temporary_password: string | null;
  }> => {
    const response = await api.post(`/admin/users/${userId}/reset-password`);
    return response.data;
  },
};
